import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import { Clock, User, Lock, Eye, EyeOff, Ban, ArrowRightLeft, Building2 } from 'lucide-react';
import { BOARD_COLUMNS } from '../utils/taskConstants';
import { parseAppDate } from '../utils/date';

const priorityLabels = { high: 'Cao', medium: 'TB', low: 'Thấp' };
const visLabels = { public: 'Public', internal: 'Internal', private: 'Private' };

function formatDeadline(deadline) {
  const date = parseAppDate(deadline);
  if (!date) return null;
  const now = new Date();
  const diff = date - now;
  const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
  const str = date.toLocaleString('vi-VN', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  return { str, isOverdue: diff < 0, isUrgent: days <= 2 && days >= 0 };
}

function TaskCard({ task, onClick, isDragging, isDisabled, onQuickMove }) {
  const deadline = formatDeadline(task.deadline);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const handleClickOutside = (event) => {
      if (!menuRef.current?.contains(event.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [menuOpen]);

  const quickMoveOptions = useMemo(
    () => BOARD_COLUMNS.filter((item) => item.id !== task.status).map((item) => ({ value: item.id, label: item.title })),
    [task.status]
  );

  const className = `task-card ${task.is_masked ? 'masked' : ''} ${isDragging ? 'dragging' : ''} ${isDisabled ? 'disabled' : ''}`;

  if (task.is_masked) {
    return (
      <div className={className} role="button" aria-disabled="true">
        <div className="task-card-badges">
          <span className="badge badge-vis-private">
            <Lock size={9} style={{ marginRight: 3 }} /> Private
          </span>
          <span className="badge badge-restricted">
            <Ban size={9} style={{ marginRight: 3 }} /> Ẩn chi tiết
          </span>
        </div>
        <div className="task-card-title private-title">Private Task</div>
      </div>
    );
  }

  return (
    <div className={className} onClick={onClick} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && onClick?.()}>
      <div className="task-card-head">
        <div className="task-card-badges">
          <span className={`badge badge-priority-${task.priority}`}>{priorityLabels[task.priority] || task.priority}</span>
          <span className={`badge badge-vis-${task.visibility}`}>
            {task.visibility === 'private' && <Lock size={9} style={{ marginRight: 3 }} />}
            {task.visibility === 'internal' && <EyeOff size={9} style={{ marginRight: 3 }} />}
            {task.visibility === 'public' && <Eye size={9} style={{ marginRight: 3 }} />}
            {visLabels[task.visibility]}
          </span>
          {task.is_restricted && (
            <span className="badge badge-restricted">
              <Ban size={9} style={{ marginRight: 3 }} /> Xem ngoài
            </span>
          )}
        </div>

        {task.can_quick_move && (
          <div className="task-card-quick-status" ref={menuRef}>
            <button
              className="btn-icon task-card-quick-btn"
              type="button"
              title="Đổi trạng thái nhanh"
              onClick={(e) => {
                e.stopPropagation();
                setMenuOpen((prev) => !prev);
              }}
              onMouseDown={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
            >
              <ArrowRightLeft size={15} />
            </button>
            {menuOpen && (
              <div className="task-card-quick-menu" onClick={(e) => e.stopPropagation()}>
                {quickMoveOptions.map((item) => (
                  <button
                    key={item.value}
                    className="task-card-quick-item"
                    type="button"
                    onClick={() => {
                      setMenuOpen(false);
                      onQuickMove?.(item.value);
                    }}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="task-card-title">{task.title}</div>
      {task.task_company && (
        <div className="task-card-company">
          <Building2 size={13} /> {task.task_company}
        </div>
      )}
      <div className="task-card-meta">
        {task.assigned_by && (
          <span className="task-card-meta-item">
            <User size={13} /> {task.assigned_by}
          </span>
        )}
        {deadline && (
          <span className={`task-card-meta-item ${deadline.isOverdue ? 'task-card-deadline' : deadline.isUrgent ? 'task-card-deadline upcoming' : ''}`}>
            <Clock size={13} /> {deadline.str}
          </span>
        )}
      </div>
    </div>
  );
}

export default memo(TaskCard);
